<template>
    <div class="text-compare" :class="`is-${statusComputed}`">
        <div class="text-container">
            <span v-html="displayValue"/>
        </div>

        <template v-if="statusComputed === `identical` || statusComputed === `applied`">
            <i class="text-valid fas fa-check-circle text-success"/>
        </template>

        <div class="actions">
            <template v-if="statusComputed === 'ignored'">
                <b-btn @click="revertIgnoreChanges" size="sm" variant="light-warning" class="ml-2">
                    <span v-tr>Ignored|Ignoré</span>
                </b-btn>
            </template>
            <template v-else-if="statusComputed === 'applied'">
                <b-btn @click="revertIgnoreChanges" size="sm" variant="light-success" class="ml-2">
                    <span v-tr>Applied|Appliqué</span>
                </b-btn>
            </template>
            <template v-else-if="statusComputed === 'identical'">
                <b-badge variant="light-success" class="md f-500">
                    <span v-tr>Identical|Identique</span>
                </b-badge>
            </template>
            <template v-else>
                <b-select v-model="display" size="sm" class="d-inline w-auto mr-2">
                    <option value="auto">Auto</option>
                    <option value="before">Before</option>
                    <option value="compare">Comparison</option>
                    <option value="after">After</option>
                </b-select>

                <b-btn @click="showDiff = true" size="sm" variant="light" class=" mr-auto">
                    <span v-tr>Compare|Comparer</span>
                </b-btn>
                <b-btn @click="ignoreChanges" size="sm" variant="light-warning" class="ml-2">
                    <span v-tr>Ignore|Ignorer</span>
                </b-btn>
                <b-btn @click="applyChanges" size="sm" variant="accent" class="ml-2">
                    <span v-tr>Apply|Appliquer</span>
                </b-btn>
            </template>
        </div>

        <b-modal v-if="showDiff" v-model="showDiff" size="xl" title="Compare Tool">
            <template v-if="reference">
                <h5>{{tr(referenceTitle)}}</h5>
                <div class="form-control h-auto">
                    <span v-html="reference"/>
                </div>
                <hr>
            </template>
            <b-row>
                <b-col cols="12" md="4">
                    <h5>Before</h5>
                    <div class="form-control h-auto">
                        <span v-html="before"/>
                    </div>
                </b-col>
                <b-col cols="12" md="4">
                    <h5>Comparison</h5>
                    <div class="form-control h-auto">
                        <span v-html="comparison"/>
                    </div>
                </b-col>
                <b-col cols="12" md="4">
                    <h5>After</h5>
                    <div class="form-control h-auto">
                        <span v-html="after"/>
                    </div>
                </b-col>
            </b-row>

            <template v-if="reason && $store.state.comparison.reason">
                <div class="reason form-help mt-4">
                    <i class="fas fa-info mr-1"/> {{reason}}
                </div>
            </template>

            <template #modal-footer>
                <b-btn variant="default" @click="showDiff = false"><span v-tr>Cancel|Annuler</span></b-btn>
                <b-btn variant="light-warning" @click="ignoreChanges"><span v-tr>Ignore|Ignorer</span></b-btn>
                <b-btn variant="primary" @click="applyChanges"><span v-tr>Apply|Appliquer</span></b-btn>
            </template>
        </b-modal>
    </div>
</template>

<script>
import {htmlDiff} from "@benedicte/html-diff";

export default {
    name: `text-compare`,
    props: {
        reference: {type: String},// original translation text for example
        referenceTitle: {type: String, default: `Reference|Référence`},
        before: {type: String},
        after: {type: String},
        reason: {type: String},
        status: {type: String},
        textDiff: {type: Object}
    },
    data() {
        return {
            display: `auto`,
            showDiff: false
        }
    },
    computed: {
        isIdentical() {
            return this.before === this.after;
        },
        comparison() {
            return htmlDiff(this.before, this.after);
        },
        statusComputed() {
            // if (this.isIdentical) {
            //     return `identical`;
            // }
            return this.status;
        },
        displayValue() {
            if (this.statusComputed === `ignored` || this.statusComputed === `identical`)
                return this.before;

            if (this.statusComputed === `applied`)
                return this.after;

            switch (this.display === `auto` ? this.$store.state.comparison.display : this.display) {
                case `before`:
                    return this.before;
                case `compare`:
                    return this.comparison;
                default:
                    return this.after;
            }
        }
    },
    methods: {
        ignoreChanges() {
            this.$emit(`ignore-changes`);
            this.showDiff = false;
        },
        revertIgnoreChanges() {
            this.$emit(`revert-ignore`);
            this.showDiff = false;
        },
        applyChanges() {
            this.$emit(`apply-changes`);
            this.showDiff = false;
        }
    }
}
</script>

<style lang="scss" scoped>
.text-compare {
    background: rgba(white, 0.9);
    color: #222;
    border-radius: 8px;
    position: relative;

    .text-container {
        padding: 5px;
    }

    .actions {
        display: flex;
        align-items: center;
        justify-content: end;
        font-size: 10pt;
        line-height: 1;
        padding: 5px;
        background: white;
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;
    }

    .text-valid {
        font-size: 18px;
        position: absolute;
        bottom: -6px;
        right: -6px;
    }

    &.is-different {
        border: 2px solid var(--color-review);
        box-shadow: 0 5px 10px rgba(130, 20, 255, 0.15);
    }

    &.is-ignored {
        border: 2px solid var(--color-warning);
    }

    .btn.btn-accent {
        background-color: var(--color-review) !important;
        border: none;
        border-color: var(--color-review) !important;
        //border-color: #6614c6 !important;
        box-shadow: 0 5px 10px rgba(130, 20, 255, 0.15);
    }

    &.is-applied,
    &.is-identical {
        border: 2px solid var(--success-color);
    }

    ::v-deep {
        del {
            color: darkred;
        }

        ins {
            color: darkgreen;
        }
    }
}
</style>
